import React, { useMemo, useState } from 'react';
import { Alert, Button, Drawer, IconButton, Stack, Typography } from '@mui/material';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { showAlert } from 'new/stores/showAlert/action';
import { TextField } from 'new/elements/TextField';
import { Select } from 'new/elements/Select';
import { useCreateAdminOrg, useFetchAdminOrgTiers } from 'new/stores/adminOrgs/adminOrgs.api';
import { renderPlanMenuItem } from './PlanMenuItem';
import { describeTierLimits, PlanOption, sortTiersByCategory } from './utils';
import styles from './CreateOrgSheet.module.scss';

function getCreateOrgErrorMessage(error: unknown): string {
  const data = (error as { data?: unknown })?.data;
  if (data && typeof data === 'object' && 'message' in data && typeof data.message === 'string') {
    return data.message;
  }
  if (typeof data === 'string') return data;
  return 'Something went wrong creating the org. Please try again.';
}

// Deliberately not '' — MUI Select can suppress rendering for an empty-string value.
const NO_PLAN_VALUE = '__no_plan__';

interface CreateOrgSheetProps {
  open: boolean;
  onClose: () => void;
}

const initialState = {
  name: '',
  emailDomain: '',
  ownerName: '',
  ownerEmail: '',
  tierId: NO_PLAN_VALUE,
};

export function CreateOrgSheet({ open, onClose }: CreateOrgSheetProps) {
  const { data: tiers = [] } = useFetchAdminOrgTiers();
  const [createOrg, { isLoading }] = useCreateAdminOrg();
  const dispatch = useAppDispatch();
  const [form, setForm] = useState(initialState);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const canSubmit = !!form.name && !!form.ownerEmail;

  const onCloseSheet = () => {
    setSubmitError(null);
    onClose();
  };

  const planItems: PlanOption[] = useMemo(
    () => [
      {
        value: NO_PLAN_VALUE,
        title: 'No plan',
        subtitle: 'Assign a plan later from Plan & Limits',
      },
      ...sortTiersByCategory(tiers).map((tier) => ({
        value: String(tier.id),
        title: tier.display_name,
        subtitle: describeTierLimits(tier),
      })),
    ],
    [tiers],
  );

  const onField =
    (field: keyof typeof initialState) => (event: React.ChangeEvent<HTMLInputElement>) =>
      setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const onSubmit = () => {
    if (!canSubmit) return;
    setSubmitError(null);
    createOrg({
      name: form.name,
      owner: { full_name: form.ownerName || form.ownerEmail, email: form.ownerEmail },
      email_domain: form.emailDomain || undefined,
      org_tier_id: form.tierId !== NO_PLAN_VALUE ? Number(form.tierId) : undefined,
    }).then((result) => {
      if ('error' in result) {
        setSubmitError(getCreateOrgErrorMessage(result.error));
        return;
      }
      dispatch(showAlert({ title: `Org "${form.name}" created`, severity: 'success' }));
      setForm(initialState);
      onCloseSheet();
    });
  };

  return (
    <Drawer anchor="right" open={open} onClose={onCloseSheet}>
      <div className={styles.container}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Create org</Typography>
          <IconButton onClick={onCloseSheet} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Stack>

        <TextField label="Org name *" value={form.name} onChange={onField('name')} />
        <TextField
          label="Email domain"
          placeholder="acme.com (auto from owner email)"
          value={form.emailDomain}
          onChange={onField('emailDomain')}
        />

        <div className={styles.row}>
          <TextField label="Owner name" value={form.ownerName} onChange={onField('ownerName')} />
          <TextField
            label="Owner email *"
            value={form.ownerEmail}
            onChange={onField('ownerEmail')}
          />
        </div>

        <div>
          <Typography variant="body2" gutterBottom>
            Plan
          </Typography>
          <Select
            fullWidth
            displayEmpty
            value={form.tierId}
            items={planItems}
            onChange={(event) => setForm((prev) => ({ ...prev, tierId: event.target.value }))}
            renderValue={(value) => planItems.find((item) => item.value === value)?.title ?? ''}
            renderItem={(item) => renderPlanMenuItem(item as PlanOption)}
          />
        </div>

        <Typography variant="body3" color="text.secondary">
          Picking a plan with a trial period automatically applies that tier's trial length as this
          org's expiration date, visible afterward in Plan &amp; Limits — but it's informational
          only. Nexla's nightly trial-expiry job only acts on self-signup orgs, and self-signup
          status can't be granted here or later, so this date won't actually pause flows or restrict
          access, and it can't be edited afterward either.
        </Typography>

        {submitError && <Alert severity="error">{submitError}</Alert>}

        <div className={styles.footer}>
          <Button variant="outlined" color="inherit" onClick={onCloseSheet}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            disabled={!canSubmit || isLoading}
            onClick={onSubmit}
          >
            {isLoading ? 'Creating…' : 'Create org'}
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
